import React from "react";
import { Link } from "react-router-dom";
import { Phone, FileText, MessageCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";

const FAQ = () => {
  const faqData = [
    {
      question: "What services do you provide?",
      answer: "We assist with fresh passport applications (normal & Tatkal), renewals, lost/damaged passports, minor passports, document verification, and appointment scheduling."
    },
    {
      question: "How can I apply for a new passport in Mumbai?",
      answer: "We verify your documents, help fill the application form, and schedule your appointment at the nearest Passport Seva Kendra or Regional Passport Office."
    },
    {
      question: "What is the difference between normal and Tatkal passports?",
      answer: (
        <div>
          <p className="mb-2"><strong>Normal Passport:</strong> Processing time ~30–45 days</p>
          <p><strong>Tatkal Passport:</strong> Fast-track service ~1–3 days</p>
        </div>
      )
    },
    {
      question: "What documents are required?",
      answer: (
        <div>
          <p className="mb-3">Document requirements vary by service type. For detailed information:</p>
          <Link to="/documents">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
              <FileText className="mr-2 h-4 w-4" />
              View Documents Page
            </Button>
          </Link>
        </div>
      )
    },
    {
      question: "How long does it take to get a passport?",
      answer: (
        <div>
          <p className="mb-2"><strong>Normal:</strong> 30–45 days</p>
          <p className="mb-3"><strong>Tatkal:</strong> 1–7 days</p>
          <p className="text-sm text-gray-600">Processing depends on document completeness and verification.</p>
        </div>
      )
    },
    {
      question: "Can you help with lost or damaged passports?",
      answer: "Yes. We assist with police reports, reapplications, and obtaining a new passport under normal or Tatkal service."
    },
    {
      question: "How can I track my application?",
      answer: "You can track using your file number or ARN. We also provide real-time updates if you apply through us."
    },
    {
      question: "Can I apply from outside Mumbai?",
      answer: "Yes, we assist clients across India. Documents can be sent online or via courier, and appointments scheduled at the nearest PSK/RPO."
    },
    {
      question: "Is Aadhaar mandatory?",
      answer: "No. Aadhaar is optional. Other valid documents: Voter ID, PAN, Driving License, or utility bills."
    },
    {
      question: "Do you provide doorstep services?",
      answer: "Yes, including document collection, form filling, and appointment scheduling."
    },
    {
      question: "What if my application is rejected?",
      answer: "We guide you to correct document errors and resubmit your application."
    },
    {
      question: "Can you apply for a passport for a minor or newborn?",
      answer: (
        <div>
          <p className="mb-3">Yes, we assist with minor passport applications. For detailed document requirements:</p>
          <Link to="/documents">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
              <FileText className="mr-2 h-4 w-4" />
              View Minor Passport Documents
            </Button>
          </Link>
        </div>
      )
    },
    {
      question: "How do I get a Tatkal passport urgently?",
      answer: (
        <div>
          <p className="mb-3">Submit the completed application, provide documents, and schedule a Tatkal appointment. We help ensure faster verification.</p>
          <a href="tel:+917977084003">
            <Button size="sm" className="bg-green-600 hover:bg-green-700">
              <Phone className="mr-2 h-4 w-4" />
              Call Us Now
            </Button>
          </a>
        </div>
      )
    },
    {
      question: "Do you assist with police verification?",
      answer: "Yes. We guide you through scheduling, document preparation, and follow-up until clearance."
    },
    {
      question: "Can I renew an expired or expiring passport?",
      answer: "Yes. We assist with renewal applications, document verification, and appointment submission."
    },
    {
      question: "What if my passport is lost while traveling abroad?",
      answer: "Contact the nearest Indian embassy/consulate immediately. We can guide you on emergency travel documents or re-issuance."
    },
    {
      question: "Do you help with application form filling?",
      answer: "Yes, our team fills the forms accurately to avoid delays or errors."
    },
    {
      question: "Can I track passport delivery?",
      answer: "Yes. Tracking details are available through postal services or your application file number. We also provide updates for applications submitted via us."
    },
    {
      question: "Why should I use a passport agent in Mumbai?",
      answer: "Agents help save time, avoid errors, get faster appointments, ensure document accuracy, and provide guidance throughout the verification process."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Passport Services in Mumbai, India
          </p>
          <p className="text-lg text-gray-500">
            Find answers to common questions about our passport services
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <Phone className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Call Us</h3>
              <p className="text-sm text-gray-600 mb-3">Speak directly with our experts</p>
              <a href="tel:+917977084003">
                <Button size="sm" className="w-full">Call Now</Button>
              </a>
            </CardContent>
          </Card>

          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <MessageCircle className="h-8 w-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">WhatsApp</h3>
              <p className="text-sm text-gray-600 mb-3">Quick chat support</p>
              <a 
                href="https://wa.me/917977084003" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="sm" className="w-full bg-green-600 hover:bg-green-700">
                  Chat Now
                </Button>
              </a>
            </CardContent>
          </Card>

          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <FileText className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Documents</h3>
              <p className="text-sm text-gray-600 mb-3">View required documents</p>
              <Link to="/documents">
                <Button size="sm" variant="outline" className="w-full">
                  View Documents
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Accordion */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">Common Questions & Answers</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="space-y-2">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="border border-gray-200 rounded-lg px-6 data-[state=open]:bg-blue-50 data-[state=open]:border-blue-300 transition-colors"
                >
                  <AccordionTrigger className="py-4 text-left font-semibold text-gray-900 hover:text-blue-600 hover:no-underline data-[state=open]:text-blue-700">
                    <span className="pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-gray-700 leading-relaxed">
                    {typeof faq.answer === 'string' ? (
                      <p>{faq.answer}</p>
                    ) : (
                      faq.answer
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <div className="mt-12 bg-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-blue-100 mb-6">
            Our passport experts are available to help you with personalized assistance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917977084003" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" className="px-8 bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
            <a href="tel:+917977084003">
              <Button 
                size="lg" 
                className="px-8 bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Us
              </Button>
            </a>
          </div>
        </div>

        {/* SEO Content */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Apply My Passport provides professional passport services across Mumbai including 
            Navi Mumbai, Mankhurd, Pydhonie, and Santacruz. We specialize in Tatkal passport, 
            fresh passport applications, renewals, and all passport-related services in Maharashtra.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;